import React, { useState, useEffect, useRef } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase-client";

const statusLabel = {
  pending: "Pending",
  waiting: "Waiting for release",
  paused: "Paused",
  processing: "Sending",
  completed: "Transferred",
  cancelled: "Cancelled",
  error: "Failed",
};
const holdLabel = {
  stalled_transfer_review:
    "Stalled transfer checked in Stripe and paused for review",
  payment_fully_refunded: "Payment fully refunded",
  payment_disputed: "Payment disputed",
  partial_refund_needs_review: "Partial refund needs review",
  payment_not_verified: "Payment could not be verified",
  club_balance_needs_review: "Club balance needs review",
  payment_settling: "Payment is settling",
  admin_review: "Paused by an admin",
  adjustment_review: "Agreed amount changed; review before resuming",
  settled_against_club_overpayment: "Settled against club overpayment",
  insufficient_funds: "Waiting for available funds",
  admin_cancelled: "Stopped by an admin",
  agreed_balance_adjustment: "Agreed balance adjustment",
};
const actionLabel = {
  recover: "Review stalled transfer",
  pause: "Pause transfer",
  resume: "Resume transfer",
  cancel: "Stop automatic transfer",
  settle: "Settle using club overpayment",
  adjust: "Record agreed remaining amount",
};
export default function PlatformClubLedger({
  row,
  busy,
  run,
  format,
  parseAmount,
}) {
  const actionHeading = useRef(null);
  const [filter, setFilter] = useState("");
  const [reviewOnly, setReviewOnly] = useState(false);
  const [limit, setLimit] = useState(20);
  const [selection, setSelection] = useState(null);
  const [note, setNote] = useState("");
  const [amount, setAmount] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  useEffect(() => {
    if (selection) actionHeading.current?.focus();
  }, [selection]);
  const choose = (action, club, entry, instruction) => {
    setSelection({ action, club, entry, instruction });
    setNote("");
    setAmount("");
    setConfirmed(false);
  };
  const submit = (event) => {
    event.preventDefault();
    const selected = selection;
    run(async () => {
      await httpsCallable(functions, "updatePlatformFinanceTransfer", {
        timeout: 300000,
      })({
        action: selected.action,
        chargeId: selected.entry.chargeId,
        expectedRemaining: selected.entry.remaining,
        expectedChargeHash: selected.entry.basisChargeHash,
        ...(selected.instruction
          ? {
              instructionId: selected.instruction.id,
              expectedInstructionHash: selected.instruction.version,
            }
          : {}),
        ...(selected.action === "adjust"
          ? { amountMinor: parseAmount(amount, row.minorUnitFactor) }
          : {}),
        note: note.trim(),
        confirm: confirmed,
      });
      setSelection(null);
      return "Financial action recorded. The ledger has been refreshed.";
    }, true);
  };
  const adjustment = parseAmount(amount, row.minorUnitFactor);
  return (
    <section
      className="platform-finance__ledger"
      aria-label="Club finance ledger"
    >
      <h4>Club finance ledger</h4>
      <p className="platform-finance__muted">
        Compare what each club should receive with what has actually left
        Stripe. Overpayments stay visible until they are reconciled.
      </p>
      <div className="platform-finance__ledger-filters">
        <label>
          Search payments
          <input
            type="search"
            value={filter}
            onChange={(event) => {
              setFilter(event.target.value);
              setLimit(20);
            }}
          />
        </label>
        <label className="platform-finance__reset-confirm">
          <input
            type="checkbox"
            checked={reviewOnly}
            onChange={(event) => setReviewOnly(event.target.checked)}
          />
          Show outstanding and review items only
        </label>
      </div>
      {(row.clubLedgers || []).map((club) => {
        const entries = club.entries.filter(
          (entry) =>
            (!reviewOnly ||
              entry.remaining > 0 ||
              entry.overpaid > 0 ||
              entry.reviewRequired ||
              entry.instructions.some((item) =>
                ["paused", "error"].includes(item.status),
              )) &&
            `${club.name} ${entry.name} ${entry.label}`
              .toLowerCase()
              .includes(filter.toLowerCase()),
        );
        if (!entries.length) return null;
        const credit = club.entries
          .filter((entry) => !entry.reviewRequired && !entry.disputed)
          .reduce((sum, entry) => sum + entry.overpaid, 0);
        return (
          <details
            key={club.teamId || club.name}
            className="platform-finance__details"
            open={!!filter || reviewOnly}
          >
            <summary>
              {club.name}
              <span>
                {club.netPosition > 0
                  ? `${format(club.netPosition)} net owed`
                  : club.netPosition < 0
                    ? `${format(-club.netPosition)} overpaid`
                    : "Balanced"}
              </span>
            </summary>
            <div className="platform-finance__details-body">
              <dl className="platform-finance__breakdown">
                <div>
                  <dt>Payments received before refunds</dt>
                  <dd>{format(club.grossReceived)}</dd>
                </div>
                <div>
                  <dt>Refunded to payers</dt>
                  <dd>{format(club.refunded)}</dd>
                </div>
                <div>
                  <dt>Club entitlement after adjustments</dt>
                  <dd>{format(club.entitlement)}</dd>
                </div>
                <div>
                  <dt>Transferred to club, less reversals</dt>
                  <dd>{format(club.transferred)}</dd>
                </div>
                <div>
                  <dt>Net recorded adjustments</dt>
                  <dd>{format(club.adjustments)}</dd>
                </div>
              </dl>
              {entries.slice(0, limit).map((entry) => (
                <details
                  key={entry.chargeId}
                  className="platform-finance__ledger-payment"
                >
                  <summary>
                    <span>
                      {entry.name} · {entry.label}
                    </span>
                    <strong>
                      {entry.remaining > 0
                        ? `${format(entry.remaining)} outstanding`
                        : entry.overpaid > 0
                          ? `${format(entry.overpaid)} overpaid`
                          : "Settled"}
                    </strong>
                  </summary>
                  <p>
                    {new Date(entry.createdAt).toLocaleDateString("en-GB")} ·
                    Received {format(entry.grossAmount)} · Refunded{" "}
                    {format(entry.refundedAmount)} · Transferred{" "}
                    {format(entry.transferred)}
                  </p>
                  {entry.adjustmentHistory?.length > 0 && (
                    <ul>
                      {entry.adjustmentHistory.map((item, index) => (
                        <li key={index}>
                          {item.note} · Adjustment {format(item.amountMinor)}
                        </li>
                      ))}
                    </ul>
                  )}
                  {entry.reviewRequired && (
                    <p className="platform-finance__alert">
                      Resolve the blocking reconciliation checks for this
                      payment before adjusting it.
                    </p>
                  )}
                  <ul className="platform-finance__ledger-instructions">
                    {entry.instructions.map((instruction) => (
                      <li key={instruction.id}>
                        <div>
                          <strong>
                            {statusLabel[instruction.status] || "Needs review"}{" "}
                            · {format(instruction.amount)}
                          </strong>
                          {instruction.reason && (
                            <p>
                              {holdLabel[instruction.reason] ||
                                "Review the transfer details"}
                            </p>
                          )}
                          {instruction.note && <p>{instruction.note}</p>}
                        </div>
                        <div className="platform-finance__actions">
                          {["pending", "waiting", "error"].includes(
                            instruction.status,
                          ) && (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() =>
                                choose("pause", club, entry, instruction)
                              }
                            >
                              Pause
                            </button>
                          )}
                          {instruction.status === "processing" &&
                            instruction.processingAt &&
                            Date.now() - Date.parse(instruction.processingAt) >
                              15 * 60000 && (
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() =>
                                  choose("recover", club, entry, instruction)
                                }
                              >
                                Review stalled transfer
                              </button>
                            )}
                          {instruction.status === "paused" && (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() =>
                                choose("resume", club, entry, instruction)
                              }
                            >
                              Resume
                            </button>
                          )}
                          {["pending", "waiting", "paused", "error"].includes(
                            instruction.status,
                          ) && (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() =>
                                choose("cancel", club, entry, instruction)
                              }
                            >
                              Stop automatic transfer
                            </button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                  {entry.remaining > 0 &&
                    !entry.disputed &&
                    !entry.reviewRequired && (
                      <div className="platform-finance__actions">
                        <button
                          type="button"
                          disabled={busy || credit < entry.remaining}
                          onClick={() => choose("settle", club, entry)}
                        >
                          Settle using club overpayment
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => choose("adjust", club, entry)}
                        >
                          Record agreed amount
                        </button>
                      </div>
                    )}
                </details>
              ))}
              {entries.length > limit && (
                <button
                  type="button"
                  onClick={() => setLimit((previous) => previous + 20)}
                >
                  Show more payments
                </button>
              )}
            </div>
          </details>
        );
      })}
      {row.orphanTransfers?.length > 0 && (
        <section aria-label="Unmatched transfer instructions">
          <h4>Unmatched transfer instructions</h4>
          <p>
            These transfers have no verified payment in the ledger. Stopping one
            keeps its funds protected for review.
          </p>
          <ul className="platform-finance__ledger-instructions">
            {row.orphanTransfers.map((instruction) => (
              <li key={instruction.id}>
                <span>
                  {instruction.clubName} · {instruction.label} ·{" "}
                  {format(instruction.amount)} ·{" "}
                  {statusLabel[instruction.status] || "Needs review"}
                </span>
                <div className="platform-finance__actions">
                  {["pending", "waiting", "error", "paused"].includes(
                    instruction.status,
                  ) && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        choose(
                          "cancel",
                          { name: instruction.clubName },
                          {
                            name: instruction.label,
                            label: "Unmatched transfer",
                            chargeId: null,
                          },
                          instruction,
                        )
                      }
                    >
                      Stop automatic transfer
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
      {selection && (
        <form
          className="platform-finance__control-form"
          onSubmit={submit}
          aria-label="Review financial action"
        >
          <h4 ref={actionHeading} tabIndex={-1}>
            {actionLabel[selection.action]}
          </h4>
          <p>
            {selection.club.name} · {selection.entry.name} ·{" "}
            {selection.entry.label}
          </p>
          {selection.action === "recover" && (
            <p>
              Checks Stripe for an already executed transfer. Records the
              completed transfer if found, or pauses the instruction for review.
              No money is sent.
            </p>
          )}
          {selection.action === "cancel" && (
            <p>
              Stops the queued transfer. The club entitlement remains protected
              until it is separately settled or adjusted.
            </p>
          )}
          {selection.action === "settle" && (
            <p>
              Uses {format(selection.entry.remaining)} of this club’s verified
              overpayment to settle this payment. No money is sent.
            </p>
          )}
          {selection.action === "adjust" && (
            <label>
              Agreed amount still owed ({row.currency})
              <input
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                disabled={busy}
              />
              <span>
                Reduces the current {format(selection.entry.remaining)}{" "}
                entitlement. The transfer will pause for review; zero settles
                it.
              </span>
            </label>
          )}
          <label>
            Reason for this action
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              minLength={10}
              maxLength={1000}
              required
              disabled={busy}
            />
          </label>
          <label className="platform-finance__reset-confirm">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
              disabled={busy}
            />
            I have checked the payment and confirm this action.
          </label>
          <div className="platform-finance__actions">
            <button
              type="submit"
              disabled={
                busy ||
                !confirmed ||
                note.trim().length < 10 ||
                (selection.action === "adjust" &&
                  (adjustment === null ||
                    adjustment >= selection.entry.remaining))
              }
            >
              Confirm {actionLabel[selection.action].toLowerCase()}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setSelection(null)}
            >
              Close
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
